import { useMemo } from 'react'
import map from 'lodash/map'
import type { SelectOption, SelectProps } from './index'
import { Select } from './Select'

/** Builds `{ label, value }` options from a plain string enum, using the enum's own value as the label. */
export const buildEnumOptions = <TEnum extends string>(enumObject: Record<string, TEnum>): SelectOption[] =>
  map(enumObject, (value) => ({ label: value, value }))

export type EnumSelectProps<TEnum extends string> = {
  enumObject: Record<string, TEnum>
  value?: TEnum
  onChange?: (value: TEnum) => void
} & Omit<SelectProps, 'value' | 'onChange' | 'options'>

/**
 * Wraps `Select` around a plain string enum whose value doubles as its display label: builds
 * `{ label, value }` options from the enum, looks up the option matching the caller's raw enum
 * `value`, and unwraps react-select's `{ label, value }` onChange payload back to the raw enum
 * before calling the caller's `onChange`.
 *
 * Needed because `Field`'s bare `onChange` auto-wiring (libs/webui/form/src/Form/Field.tsx) stores
 * whatever the wrapped component's `onChange` is called with verbatim into Formik state — for
 * react-select that would be the `{ label, value }` option object, not the plain enum a mutation
 * input expects. This component does the conversion at its own boundary so callers can still use
 * `Field`'s bare `onChange` flag and never see the option-object shape.
 *
 * Only fits an enum consumed this way end-to-end (raw enum in Formik state). A component whose
 * `value`/`onChange` must instead stay an untransformed option object — e.g. because it is plugged
 * into `SelectInlineInput` as its `selectComponent`, which does its own lookup and unwrap — cannot
 * use this; it should call `buildEnumOptions` directly and forward `value`/`onChange` untouched.
 */
export function EnumSelect<TEnum extends string>({ enumObject, value, onChange, ...rest }: EnumSelectProps<TEnum>) {
  const options = useMemo(() => buildEnumOptions(enumObject), [enumObject])
  const selectedOption = options.find((option) => option.value === value) ?? null

  return (
    <Select
      {...rest}
      options={options}
      value={selectedOption}
      onChange={(selected: SelectOption) => onChange?.(selected?.value as TEnum)}
    />
  )
}
